import { useCallback, useEffect, useRef, useState } from "react"

import {
  getListRows,
  getMapFeatures
} from "../../../GlobalMap/api/global-map.api"
import type {
  MapFeature,
  MapFeaturesResponse,
  ProjectOrLeadListRow
} from "../../../GlobalMap/redux/global-map.types"
import { useAppSelector } from "../../../../hooks/use-app-selector"
import { selectActiveBranchId } from "../../../Branches/redux/branches.slice"

export type LocationScope = "all" | "project" | "lead"

export interface HomeLocationsData {
  features: MapFeature[]
  /** The whole response, so the map can fit itself with `computeFit`. */
  response: MapFeaturesResponse | null
  rows: ProjectOrLeadListRow[]
  counts: { all: number; project: number; lead: number }
  /** Records the server holds but could not place on the map. */
  unmapped: number
  state: "loading" | "ready" | "error" | "empty"
  retry: () => void
}

/** The whole portfolio, not a viewport: Home has no map the user has panned. */
const UNBOUNDED_ZOOM = 4
const ROW_LIMIT = 10

const isProjectOrLead = (row: {
  entity_type: string
}): row is ProjectOrLeadListRow =>
  row.entity_type === "project" || row.entity_type === "lead"

/**
 * The toolbar's All / Projects / Leads counts.
 *
 * From the LIST endpoint's totals, which count every authorized record. The
 * map endpoint's `project_total` / `lead_total` count only what it could
 * place, so a company whose records have no address read "All 0" beside a
 * list full of projects. The map's figures are only the fallback for a list
 * that did not answer.
 */
export const locationCounts = (
  list: { project_total?: number; lead_total?: number } | null | undefined,
  map: { project_total?: number; lead_total?: number } | null | undefined
): { all: number; project: number; lead: number } => {
  const source =
    list &&
    typeof list.project_total === "number" &&
    typeof list.lead_total === "number"
      ? list
      : map
  const project = source?.project_total ?? 0
  const lead = source?.lead_total ?? 0
  return { all: project + lead, project, lead }
}

/**
 * Home's own Locations data.
 *
 * Deliberately NOT `useMapFeatures`. That hook is bound to the Global Map's
 * redux slice — its viewport, its committed filters, its search term, its
 * layer toggles — so mounting it here would make Home's section and the Map
 * page fight over one piece of state, and panning one would refetch the
 * other. This calls the same authorized endpoints directly with Home's own
 * scope instead.
 *
 * BRANCH SCOPE (§10). `team` goes on the request AND into the effect's
 * dependencies, and every in-flight response is stamped with the branch it
 * was asked for. A response that arrives after the branch changed is dropped
 * rather than rendered, so switching branches can never leave the previous
 * branch's projects on the map.
 */
export const useHomeLocations = (scope: LocationScope): HomeLocationsData => {
  const branchId = useAppSelector(selectActiveBranchId) ?? undefined
  const [features, setFeatures] = useState<MapFeature[]>([])
  const [response, setResponse] = useState<MapFeaturesResponse | null>(null)
  const [rows, setRows] = useState<ProjectOrLeadListRow[]>([])
  const [counts, setCounts] = useState({ all: 0, project: 0, lead: 0 })
  const [unmapped, setUnmapped] = useState(0)
  const [state, setState] = useState<HomeLocationsData["state"]>("loading")
  const [attempt, setAttempt] = useState(0)

  // Monotonic: only the newest request may write. Guards the ordinary
  // out-of-order case as well as the branch switch.
  const requestRef = useRef(0)

  const entities = scope === "all" ? "project,lead" : scope

  useEffect(() => {
    const id = ++requestRef.current
    let cancelled = false
    setState("loading")

    const run = async () => {
      try {
        const [mapResponse, listResponse] = await Promise.all([
          getMapFeatures({
            zoom: UNBOUNDED_ZOOM,
            entities,
            ...(branchId ? { team: branchId } : {})
          }),
          getListRows({
            entities,
            list_entity: scope,
            sort: "relevant_date",
            direction: "asc",
            page: 1,
            page_size: ROW_LIMIT,
            ...(branchId ? { team: branchId } : {})
          })
        ])

        if (cancelled || id !== requestRef.current) return

        if (!mapResponse.ok || !mapResponse.data) {
          setState("error")
          return
        }

        const data = mapResponse.data
        setResponse(data)
        setFeatures(data.features ?? [])
        setUnmapped(data.unmapped ?? 0)
        setCounts(
          locationCounts(listResponse.ok ? listResponse.data : null, data)
        )

        // A failed list is not an empty list: the map still renders, and the
        // list side says it could not load rather than reading as "no records".
        const listRows = listResponse.ok
          ? (listResponse.data?.rows ?? []).filter(isProjectOrLead)
          : []
        setRows(listRows)

        const nothing =
          (data.features ?? []).length === 0 && listRows.length === 0
        setState(nothing ? "empty" : "ready")
      } catch {
        if (!cancelled && id === requestRef.current) setState("error")
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [entities, scope, branchId, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  return { features, response, rows, counts, unmapped, state, retry }
}
